/**
 * `useCanI` — frontend mirror of the server's `requirePermission`
 * middleware. Reads the SAME `PERMISSIONS` matrix the backend uses
 * (`@ottotester/permissions`) so UI affordances cannot drift from API
 * enforcement. UI trimming is UX, not security — the backend gate is
 * what protects the data; this hook just keeps users from clicking
 * buttons that would dead-end at a 403.
 *
 * Usage:
 *
 *   const canEditCredentials = useCanI('credentials.write', orgId)
 *   {canEditCredentials && <EditButton />}
 *
 * The `orgId` is required because role is per-(user, org). Callers
 * inside an org-scoped route (org settings, workspace settings) read
 * the org id from the route params or from `useIdentity().orgs[]`
 * matched against the active context. There's no implicit "current
 * org" — passing the wrong id silently returns `false`, which is
 * fail-safe (no affordance shown when in doubt).
 *
 * Identity-aware constraints (last-owner, can't-act-on-self, etc.)
 * are NOT encoded here — same matrix-only design as the backend
 * (see ADR 0001). Callers that need those checks compose them in
 * the JSX:
 *
 *   const canChange = useCanI('members.changeRole', orgId)
 *   const isMe = m.userId === currentUser.id
 *   {canChange && !isMe && <ChangeRoleButton />}
 *
 * Why a hook instead of a plain function: identity-state subscription.
 * When the user switches orgs (changing their effective role) or when
 * `refreshIdentity()` updates the role list (e.g. after a role
 * change), every `useCanI` consumer re-renders with the new answer.
 * A non-hook helper would silently render stale affordances after a
 * role change.
 */
import {
  hasPermission,
  hasWorkspacePermission,
  type Action,
  type WorkspaceAction,
} from '@ottotester/permissions'
import { useIdentity } from './useIdentity'

/** Returns true iff the signed-in user's role in the given org allows
 * the requested action. Returns false when:
 *   - identity isn't loaded yet (no role yet → no affordance)
 *   - user has no membership in `orgId` (not a member → no affordance)
 *   - matrix says the role doesn't allow the action
 *
 * All three failure modes share the same answer (`false`) on purpose —
 * the UI shouldn't differentiate "loading" from "denied" for an
 * affordance check; the affordance simply isn't visible until the
 * answer is "yes." Callers that need to render a loading state
 * separately should subscribe to `useIdentity()` directly. */
export function useCanI(action: Action, orgId: string | null | undefined): boolean {
  const { state } = useIdentity()
  if (state.status !== 'ready' || !orgId) return false
  const role = state.identity.orgs.find(o => o._id === orgId)?.role
  return hasPermission(role, action)
}

/** Workspace-tier companion to `useCanI` (#526 PR 5a — hardened from
 * the PR 3 stub).
 *
 * Reads `identity.workspaceRoles[workspaceId]` and dispatches to
 * `hasWorkspacePermission`. Returns false when:
 *   - identity isn't loaded yet (no role yet → no affordance)
 *   - no workspaceId is supplied
 *   - identity has no entry for this workspace (user can't reach it
 *     — direct grant missing AND not the org owner; consistent with
 *     the backend's `requireWorkspaceMember` 403)
 *   - identity is from a pre-PR-5a server build (no `workspaceRoles`
 *     field in the payload at all)
 *   - matrix says the role doesn't allow the action
 *
 * All failure modes share `false` on purpose — same shape as the
 * org-tier hook. Backend (`requireWorkspaceMember` +
 * `requireWorkspacePermission`) remains the authoritative gate; this
 * hook just keeps the UI honest about which affordances make sense.
 *
 * Team-mediated grants are NOT yet reflected in `workspaceRoles` —
 * the server-side computation in `/api/me` only consults direct
 * grants + Owner implicit. PR 5b will widen `/api/me` to fold team
 * grants into the same map (and PR 4 lands the corresponding
 * middleware widening at `requireWorkspaceMember` so the backend
 * starts honoring team-mediated access too). Until both ship, a
 * user whose ONLY workspace access is via a team:
 *   - As of PR 5a (before PR 4 merges): backend 403s them too —
 *     the middleware still only consults direct grants + Owner
 *     implicit. The frontend hiding the affordance is correct.
 *   - Once PR 4 merges: backend admits them via team grants but
 *     the affordance still hides until PR 5b extends /api/me.
 *     Worst case is a hidden affordance for an admin-via-team
 *     user — they can still navigate to the underlying URL and
 *     the backend will let them through.
 * PR #540 Copilot review (comment 3). */
export function useCanIWorkspace(
  action: WorkspaceAction,
  workspaceId: string | null | undefined,
): boolean {
  const { state } = useIdentity()
  if (state.status !== 'ready' || !workspaceId) return false
  const workspaceRole = state.identity.workspaceRoles?.[workspaceId]
  return hasWorkspacePermission(workspaceRole, action)
}
